import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  Stack,
  Button,
  Divider,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { customFieldsApi } from '../services/api';

// Компонент текстового поля с автокомплитом
const AutocompleteTextField = ({ 
  value, 
  onChange, 
  label, 
  placeholder, 
  projectId, 
  fieldName, 
  onLoadSuggestions,
  multiline = false,
  rows = 1
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [cache, setCache] = useState(new Map());

  // Загружаем подсказки при изменении ввода
  const loadSuggestions = useCallback(async (query) => {
    if (!projectId || !fieldName) {
      setSuggestions([]);
      return;
    }

    // Если запрос пустой, загружаем все значения
    if (!query || query.trim() === '') {
      query = '';
    }

    // Проверяем кэш
    const cacheKey = `${projectId}-${fieldName}-${query}`;
    if (cache.has(cacheKey)) {
      setSuggestions(cache.get(cacheKey));
      return;
    }

    setLoading(true);
    try {
      const response = await customFieldsApi.searchFieldValues(projectId, fieldName, query);
      const suggestions = response.data || [];
      
      // Сохраняем в кэш
      setCache(prev => new Map(prev).set(cacheKey, suggestions));
      setSuggestions(suggestions);
    } catch (error) {
      console.error('Ошибка загрузки подсказок:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, fieldName, cache]);

  // Debounce для загрузки подсказок
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue !== value) {
        loadSuggestions(inputValue);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, loadSuggestions, value]);

  // Загружаем все значения при фокусе на поле (если нет ввода)
  const handleFocus = useCallback(() => {
    if (!inputValue && projectId && fieldName) {
      loadSuggestions('');
    }
  }, [inputValue, projectId, fieldName, loadSuggestions]);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event, newValue) => {
    onChange(newValue || '');
    setInputValue(newValue || '');
  };

  return (
    <Autocomplete
      freeSolo
      options={suggestions}
      value={value || ''}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onFocus={handleFocus}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
          fullWidth
          variant="outlined"
          multiline={multiline}
          rows={rows}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText="Нет подсказок"
      loadingText="Загрузка подсказок..."
    />
  );
};

const FIELD_TYPE_COMPONENTS = {
  TEXT: ({ value, onChange, label, placeholder, projectId, fieldName, onLoadSuggestions }) => (
    <AutocompleteTextField
      value={value || ''}
      onChange={onChange}
      label={label}
      placeholder={placeholder}
      projectId={projectId}
      fieldName={fieldName}
      onLoadSuggestions={onLoadSuggestions}
    />
  ),
  
  TEXTAREA: ({ value, onChange, label, placeholder, projectId, fieldName, onLoadSuggestions }) => (
    <AutocompleteTextField
      value={value || ''}
      onChange={onChange}
      label={label}
      placeholder={placeholder}
      projectId={projectId}
      fieldName={fieldName}
      onLoadSuggestions={onLoadSuggestions}
      multiline
      rows={2}
    />
  ),
  
  DATETIME: ({ value, onChange, label }) => (
    <TextField
      label={label}
      type="datetime-local"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      variant="outlined"
      InputLabelProps={{ shrink: true }}
    />
  ),
  
  MULTISELECT: ({ value, onChange, label, options, inputValue = '', onInputChange }) => {
    const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
    const listOptions = Array.isArray(options) ? options : [];

    return (
      <Autocomplete
        multiple
        options={listOptions}
        value={selectedValues}
        onChange={(_, newValue) => {
          onChange(newValue);
          onInputChange?.('');
        }}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => onInputChange?.(newInputValue || '', reason)}
        size="small"
        disableCloseOnSelect
        filterSelectedOptions
        isOptionEqualToValue={(option, val) => option === val}
        noOptionsText="Нет вариантов"
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option}
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Начните вводить для поиска"
          />
        )}
      />
    );
  },

  LIST: ({ value, onChange, label, options, inputValue = '', onInputChange }) => (
    <Autocomplete
      options={Array.isArray(options) ? options : []}
      value={value || null}
      onChange={(_, newValue) => onChange(newValue || '')}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => onInputChange?.(newInputValue || '', reason)}
      size="small"
      noOptionsText="Нет вариантов"
      isOptionEqualToValue={(option, val) => option === val}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Начните вводить для поиска"
        />
      )}
    />
  ),
  
  CHECKBOX: ({ value, onChange, label }) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          indeterminate={value === null}
        />
      }
      label={label}
    />
  )
};

export default function CustomFieldsFilter({ 
  projectId, 
  isOpen, 
  onToggle, 
  filters, 
  onFiltersChange,
  onClearFilters
}) {
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldInputs, setFieldInputs] = useState({});

  // Загружаем кастомные поля проекта
  useEffect(() => {
    if (!projectId) {
      setCustomFields([]);
      setFieldInputs({});
      return;
    }

    const loadCustomFields = async () => {
      setLoading(true);
      try {
        const response = await customFieldsApi.getProjectFields(projectId);
        const fields = response.data || [];
        // Фильтруем только активные поля
        const activeFields = fields.filter(field => field.isActive);
        setCustomFields(activeFields);
        setFieldInputs({});
      } catch (error) {
        console.error('Ошибка загрузки кастомных полей:', error);
        setCustomFields([]);
        setFieldInputs({});
      } finally {
        setLoading(false);
      }
    };

    loadCustomFields();
  }, [projectId]);

  // Очищаем кэш подсказок при смене проекта
  useEffect(() => {
    // Очищаем кэш при смене проекта
    if (projectId) {
      // Можно добавить логику очистки кэша если нужно
    }
  }, [projectId]);

  // Обработчик изменения значения фильтра
  const handleFilterChange = (fieldName, value) => {
    const newFilters = { ...filters };
    if (value === '' || value === null || (Array.isArray(value) && value.length === 0)) {
      delete newFilters[fieldName];
    } else {
      newFilters[fieldName] = value;
    }
    onFiltersChange(newFilters);
  };

  // Очистка всех фильтров
  const handleClearAll = () => {
    onClearFilters();
    setFieldInputs({});
  };

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = Object.keys(filters).length > 0;

  if (!projectId) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Кнопка открытия/закрытия фильтра */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton
          onClick={onToggle}
          size="small"
          color={hasActiveFilters ? "primary" : "default"}
        >
          <FilterListIcon />
        </IconButton>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            cursor: 'pointer',
            color: hasActiveFilters ? 'primary.main' : 'text.primary',
            display: 'flex',
            alignItems: 'center',
            ml: 1
          }}
          onClick={onToggle}
        >
                     Поиск по дополнительным полям
           {hasActiveFilters && (
             <Chip 
               label={Object.keys(filters).length} 
               size="small" 
               color="primary" 
               sx={{ ml: 1 }}
             />
           )}
        </Typography>
        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      {/* Панель фильтров */}
      <Collapse in={isOpen}>
        <Box sx={{ 
          p: 2, 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          backgroundColor: 'background.paper'
        }}>
                     {loading ? (
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <CircularProgress size={16} />
               <Typography variant="body2" color="text.secondary">
                 Загрузка полей...
               </Typography>
             </Box>
           ) : customFields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Для данного проекта не настроены дополнительные поля
            </Typography>
          ) : (
            <>
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                 <Typography variant="h6" component="h3">
                   Фильтры по дополнительным полям
                 </Typography>
                                   {hasActiveFilters && (
                    <Button
                      startIcon={<ClearIcon />}
                      onClick={handleClearAll}
                      size="small"
                      color="error"
                      variant="outlined"
                    >
                      Очистить дополнительные
                    </Button>
                  )}
               </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                 {customFields.map((field) => {
                   const FieldComponent = FIELD_TYPE_COMPONENTS[field.type];
                   const currentValue = filters[field.name] || '';
                   const currentInput = fieldInputs[field.id] || '';
                   
                   if (!FieldComponent) {
                     return null;
                   }

                   return (
                     <Box key={field.id}>
                       <FieldComponent
                         label={field.label}
                         value={currentValue}
                         onChange={(value) => handleFilterChange(field.name, value)}
                         inputValue={currentInput}
                         onInputChange={(val, reason) => {
                           if (reason === 'reset') return;
                           setFieldInputs(prev => ({ ...prev, [field.id]: val }));
                         }}
                         placeholder={field.helpText || `Введите ${field.label.toLowerCase()}`}
                         options={field.options}
                         fieldName={field.name}
                         projectId={projectId}
                         onLoadSuggestions={() => {}}
                       />
                     </Box>
                   );
                 })}
              </Stack>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
